import { createContext, createElement, useContext, useEffect, useState, type ReactNode } from "react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { porukaDohvata } from "@/lib/greske";

export interface Postavke {
  kontrola_osposobljenosti: boolean;
}
export interface UcinakUkljucenja {
  aktivni_rasporedi: number;
  radnici_bez_sati: number;
}
const changed = "avm:postavke-promijenjene";

export async function spremiPostavke(data: Postavke & { razlog: string }) {
  const result = (await api.patch<Postavke>("/postavke", data)).data;
  window.dispatchEvent(new Event(changed));
  return result;
}

export async function ucinakUkljucenja() {
  return (await api.get<UcinakUkljucenja>("/postavke/ucinak-ukljucenja")).data;
}

/** Shared policy reader. Unknown/offline policy never silently disables controls.
 * No module cache: signing out cannot leak a previous user's settings.
 */
function useRemotePostavke(enabled = true) {
  const { user } = useAuth();
  const [state, setState] = useState<{
    data: Postavke | null;
    error: string | null;
    userId: string | undefined;
  }>({ data: null, error: null, userId: user?.id });
  useEffect(() => {
    if (!enabled) return;
    let active = true;
    let request = 0;
    const load = async () => {
      const current = ++request;
      try {
        const { data } = await api.get<Postavke>("/postavke");
        if (typeof data.kontrola_osposobljenosti !== "boolean") {
          throw new Error("Postavke nisu dostupne.");
        }
        if (active && current === request)
          setState({ data, error: null, userId: user?.id });
      } catch (error: unknown) {
        if (active && current === request)
          setState({ data: null, error: porukaDohvata(error), userId: user?.id });
      }
    };
    void load();
    window.addEventListener(changed, load);
    window.addEventListener("focus", load);
    return () => {
      active = false;
      window.removeEventListener(changed, load);
      window.removeEventListener("focus", load);
    };
  }, [user?.id, enabled]);
  const current = state.userId === user?.id;
  const data = current ? state.data : null;
  return {
    postavke: data,
    prijelazno: data?.kontrola_osposobljenosti === false,
    loading: !data && !(current && state.error),
    error: current ? state.error : null,
  };
}

const PostavkeContext = createContext<ReturnType<typeof useRemotePostavke> | null>(null);

export function PostavkeProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const value = useRemotePostavke(Boolean(user));
  return createElement(PostavkeContext.Provider, { value }, children);
}

export function usePostavke() {
  const shared = useContext(PostavkeContext);
  const local = useRemotePostavke(shared === null);
  return shared ?? local;
}
